import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  runTransaction,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore'
import defaultDataTemplate from '../../data/defaultDataTemplate.json'
import { firestore } from '../firebase/firebaseClient'
import { extractDataTemplate, mergeRemoteDataWithTemplate } from './dataTemplate'
import {
  CATEGORY_DOCUMENTS,
  FIRESTORE_COLLECTIONS,
  GENERAL_INFO_DOCUMENTS,
  LOCAL_COLLECTION_TO_FIRESTORE,
  firestoreCollectionForLocalKey,
  storageFolderForCollection
} from './firebaseModel'

const CONTENT_COLLECTION_KEYS = new Set(['clinics', 'services', 'products', 'articles'])
const FEEDBACK_COLLECTION_KEYS = new Set(['orders', 'CallBack', 'ContactMessages'])
const CATEGORY_KEYS = new Set(Object.keys(CATEGORY_DOCUMENTS))

export class StaleDataError extends Error {
  constructor(message = 'Документ был изменен другим администратором. Обновите данные и повторите правку.') {
    super(message)
    this.name = 'StaleDataError'
    this.code = 'stale-data'
  }
}

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function reverseMap(map) {
  return Object.fromEntries(Object.entries(map).map(([key, value]) => [value, key]))
}

function timestampToken(value) {
  if (!value) return ''
  if (typeof value.toMillis === 'function') return String(value.toMillis())
  if (typeof value.seconds === 'number') return `${value.seconds}:${value.nanoseconds || 0}`
  if (typeof value._seconds === 'number') return `${value._seconds}:${value._nanoseconds || 0}`
  return String(value)
}

function assertFresh(remoteData, expectedUpdatedAt) {
  if (expectedUpdatedAt === undefined) return
  const expected = timestampToken(expectedUpdatedAt)
  const actual = timestampToken(remoteData && remoteData.updatedAt)
  if (expected !== actual) throw new StaleDataError()
}

function withoutMeta(value) {
  if (!value || typeof value !== 'object') return value
  const next = clone(value)
  delete next.updatedAt
  delete next.createdAt
  return next
}

function stripImageAltFields(value) {
  if (Array.isArray(value)) return value.map(stripImageAltFields)
  if (!value || typeof value !== 'object') return value
  if (typeof value.toMillis === 'function') return value
  if (value instanceof Date) return value

  const result = {}
  Object.entries(value).forEach(([key, childValue]) => {
    if (key === 'alt') return
    result[key] = stripImageAltFields(childValue)
  })
  return result
}


function docToItem(item) {
  return {
    id: item.id,
    ...item.data()
  }
}

function assertContentCollection(collectionKey) {
  if (!CONTENT_COLLECTION_KEYS.has(collectionKey)) {
    throw new Error(`Запись коллекции ${collectionKey} запрещена этим редактором.`)
  }
}

function assertReadableCollection(collectionKey) {
  if (!CONTENT_COLLECTION_KEYS.has(collectionKey) && !FEEDBACK_COLLECTION_KEYS.has(collectionKey)) {
    throw new Error(`Коллекция ${collectionKey} не описана в модели данных.`)
  }
}

function assertCategoryKey(categoryKey) {
  if (!CATEGORY_KEYS.has(categoryKey)) {
    throw new Error(`Категория ${categoryKey} не описана в модели данных.`)
  }
}

async function loadDefaultData() {
  return extractDataTemplate(defaultDataTemplate)
}

async function loadCollectionItems(collectionName) {
  const snapshot = await getDocs(collection(firestore, collectionName))
  return snapshot.docs.map(docToItem)
}

async function loadCategories() {
  const result = {}
  const versions = {}
  const snapshot = await getDocs(collection(firestore, FIRESTORE_COLLECTIONS.categories))
  const docs = Object.fromEntries(snapshot.docs.map(item => [item.id, item]))

  Object.entries(CATEGORY_DOCUMENTS).forEach(([localKey, documentId]) => {
    const sourceDoc = docs[documentId]
    if (!sourceDoc) return
    const data = sourceDoc.data()
    result[localKey] = data.items || []
    versions[localKey] = data.updatedAt || null
  })

  return { data: result, versions }
}

async function loadGeneralInfo() {
  const [generalSnapshot, pagesSnapshot] = await Promise.all([
    getDoc(doc(firestore, FIRESTORE_COLLECTIONS.generalInfo, GENERAL_INFO_DOCUMENTS.general)),
    getDoc(doc(firestore, FIRESTORE_COLLECTIONS.generalInfo, GENERAL_INFO_DOCUMENTS.pages))
  ])
  const generalDoc = generalSnapshot.exists() ? generalSnapshot.data() : null
  const pagesDoc = pagesSnapshot.exists() ? pagesSnapshot.data() : null

  return {
    data: {
      general: generalDoc ? { sections: generalDoc.sections || [] } : null,
      pages: pagesDoc ? pagesDoc.pages || {} : null
    },
    versions: {
      general: generalDoc ? generalDoc.updatedAt || null : null,
      pages: pagesDoc ? pagesDoc.updatedAt || null : null
    }
  }
}

function mergeVersionsWithItems(items) {
  const versions = {}
  items.forEach(item => {
    versions[item.id] = item.updatedAt || null
  })
  return versions
}

async function fetchDocData(docRef) {
  const snapshot = await getDocFromServer(docRef)
  return snapshot.exists() ? stripImageAltFields({ id: snapshot.id, ...snapshot.data() }) : null
}

function createVersionedResult(data, versions) {
  return { data, versions }
}

export function createFirebaseDataRepository() {
  const firestoreToLocalCollection = reverseMap(LOCAL_COLLECTION_TO_FIRESTORE)

  return {
    async loadDb() {
      const defaultData = await loadDefaultData()
      const [categoriesResult, generalInfoResult, collectionEntries] = await Promise.all([
        loadCategories(),
        loadGeneralInfo(),
        Promise.all(Object.values(LOCAL_COLLECTION_TO_FIRESTORE).map(async firestoreCollection => {
          const localCollection = firestoreToLocalCollection[firestoreCollection]
          const items = await loadCollectionItems(firestoreCollection)
          return [localCollection, { items }, mergeVersionsWithItems(items)]
        }))
      ])

      const remoteData = {
        version: defaultData.version,
        languages: defaultData.languages,
        categories: categoriesResult.data,
        general: generalInfoResult.data.general,
        pages: generalInfoResult.data.pages,
        collections: Object.fromEntries(collectionEntries.map(([key, value]) => [key, value]))
      }
      const versions = {
        categories: categoriesResult.versions,
        general: generalInfoResult.versions.general,
        pages: generalInfoResult.versions.pages,
        collections: Object.fromEntries(collectionEntries.map(([key, , itemVersions]) => [key, itemVersions]))
      }

      return createVersionedResult(stripImageAltFields(mergeRemoteDataWithTemplate(defaultData, remoteData)), versions)
    },

    async createCollectionItem(collectionKey, item) {
      assertContentCollection(collectionKey)
      const firestoreCollection = firestoreCollectionForLocalKey(collectionKey)
      const cleanItem = stripImageAltFields(withoutMeta(item))
      const itemRef = doc(firestore, firestoreCollection, cleanItem.id)

      await runTransaction(firestore, async transaction => {
        const current = await transaction.get(itemRef)
        if (current.exists()) throw new Error('Запись с таким ID уже существует. Обновите список и повторите создание.')
        transaction.set(itemRef, {
          ...cleanItem,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      })

      return fetchDocData(itemRef)
    },

    async updateCollectionItem(collectionKey, item, expectedUpdatedAt) {
      assertContentCollection(collectionKey)
      const firestoreCollection = firestoreCollectionForLocalKey(collectionKey)
      const cleanItem = stripImageAltFields(withoutMeta(item))
      const itemRef = doc(firestore, firestoreCollection, cleanItem.id)

      await runTransaction(firestore, async transaction => {
        const current = await transaction.get(itemRef)
        if (!current.exists()) throw new Error('Запись была удалена. Обновите список перед сохранением.')
        assertFresh(current.data(), expectedUpdatedAt)
        transaction.set(itemRef, {
          ...cleanItem,
          updatedAt: serverTimestamp()
        }, { merge: true })
      })

      return fetchDocData(itemRef)
    },

    async deleteCollectionItem(collectionKey, itemId, expectedUpdatedAt) {
      assertContentCollection(collectionKey)
      const firestoreCollection = firestoreCollectionForLocalKey(collectionKey)
      const itemRef = doc(firestore, firestoreCollection, itemId)

      await runTransaction(firestore, async transaction => {
        const current = await transaction.get(itemRef)
        if (!current.exists()) return
        assertFresh(current.data(), expectedUpdatedAt)
        transaction.delete(itemRef)
      })
    },

    async saveCategoryList(categoryKey, items, expectedUpdatedAt) {
      assertCategoryKey(categoryKey)
      const documentId = CATEGORY_DOCUMENTS[categoryKey]
      const categoryRef = doc(firestore, FIRESTORE_COLLECTIONS.categories, documentId)

      await runTransaction(firestore, async transaction => {
        const current = await transaction.get(categoryRef)
        if (current.exists()) assertFresh(current.data(), expectedUpdatedAt)
        transaction.set(categoryRef, {
          items: stripImageAltFields(clone(items || [])),
          updatedAt: serverTimestamp()
        }, { merge: true })
      })

      const saved = await getDoc(categoryRef)
      const data = saved.exists() ? saved.data() : { items: [] }
      return { items: data.items || [], updatedAt: data.updatedAt || null }
    },

    async savePage(pageKey, content, expectedUpdatedAt) {
      const pagesRef = doc(firestore, FIRESTORE_COLLECTIONS.generalInfo, GENERAL_INFO_DOCUMENTS.pages)

      await runTransaction(firestore, async transaction => {
        const current = await transaction.get(pagesRef)
        const currentData = current.exists() ? current.data() : { pages: {} }
        assertFresh(currentData, expectedUpdatedAt)
        transaction.set(pagesRef, {
          pages: {
            ...(currentData.pages || {}),
            [pageKey]: stripImageAltFields(clone(content))
          },
          updatedAt: serverTimestamp()
        }, { merge: true })
      })

      const saved = await getDoc(pagesRef)
      const data = saved.exists() ? saved.data() : { pages: {} }
      return { page: (data.pages || {})[pageKey] || null, updatedAt: data.updatedAt || null }
    },

    async saveGeneralSection(sectionKey, content, expectedUpdatedAt) {
      const generalRef = doc(firestore, FIRESTORE_COLLECTIONS.generalInfo, GENERAL_INFO_DOCUMENTS.general)

      await runTransaction(firestore, async transaction => {
        const current = await transaction.get(generalRef)
        const currentData = current.exists() ? current.data() : { sections: [] }
        assertFresh(currentData, expectedUpdatedAt)
        const sections = Array.isArray(currentData.sections) ? clone(currentData.sections) : []
        const index = sections.findIndex(section => section && section.key === sectionKey)
        const nextSection = { key: sectionKey, content: stripImageAltFields(clone(content)) }
        if (index >= 0) sections.splice(index, 1, nextSection)
        else sections.push(nextSection)
        transaction.set(generalRef, {
          sections,
          updatedAt: serverTimestamp()
        }, { merge: true })
      })

      const saved = await getDoc(generalRef)
      const data = saved.exists() ? saved.data() : { sections: [] }
      return { general: { sections: data.sections || [] }, updatedAt: data.updatedAt || null }
    },

    async updateFeedbackField(collectionKey, itemId, patch) {
      assertReadableCollection(collectionKey)
      const collectionName = firestoreCollectionForLocalKey(collectionKey)
      await updateDoc(doc(firestore, collectionName, itemId), {
        ...patch,
        updatedAt: serverTimestamp()
      })
    },

    async exportDb() {
      const result = await this.loadDb()
      return stripImageAltFields(result.data)
    }
  }
}

export {
  CATEGORY_DOCUMENTS,
  FIRESTORE_COLLECTIONS,
  GENERAL_INFO_DOCUMENTS,
  firestoreCollectionForLocalKey,
  storageFolderForCollection
}
